import { Directive, ElementRef, HostListener, OnInit, AfterViewInit } from '@angular/core';
import { isMaskedDob } from '../utils/date-of-birth';

/**
 * Format dates into the MM/dd/YYYY format. This is used to display dates to the user, not
 * to save them to the database
 */
@Directive({
  selector: '[appDateFormat]',
})
export class DateFormatDirective implements OnInit, AfterViewInit {
  /**
   * Constructor to initialize services and references
   */
  constructor(
    private _elementRef: ElementRef,
  ) { }

  /**
   * OnInit lifecycle hook to format the date value
   */
  ngOnInit(): void {
    this._formatValue();
  }

  /**
   * AfterViewInit lifecycle hook to ensure formatting after view initialization
   */
  ngAfterViewInit(): void {
    // Format again after view init to catch any values set by form.patchValue
    setTimeout(() => {
      this._formatValue();
    });
  }

  /**
   * Format the current value of the input
   */
  private _formatValue(): void {
    const value = this._elementRef.nativeElement.value;
    if (!value || isMaskedDob(value)) {
      return;
    }

    const parts = value.replace(/[^\d\/]/g, "").split('/');
    this._elementRef.nativeElement.value = this._transform(parts[0] || "", parts[1] || "", parts[2] || "");
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input'));
  }

  /**
   * On blur event, format the date value
   */
  @HostListener('blur', ['$event.target.value'])
  onBlur(value: string): void {
    if (!value || isMaskedDob(value)) {
      return;
    }

    const parts = value.replace(/[^\d\/]/g, "").split('/');
    this._elementRef.nativeElement.value = this._transform(parts[0] || "", parts[1] || "", parts[2] || "");
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input'));
  }

  /**
   * Transform the input value to the date format
   * 
   * @param digits The string of digits to format
   * @return The formatted date string (digits only)
  */
  private _transform(month: string, day: string, year: string): string {
    // Format month digits
    let m = "__/";
    if (month.length === 2) {
      // Month has two digits and is complete
      m = month + "/";
    } else if (month.length === 1) {
      if (['0', '1'].includes(month) && day === "") {
        // Month has one digit and day is not present => is ambiguous
        m = month;
      } else {
        // Month has one digit and is not ambiguous
        m = "0" + month + "/";
      }
    }

    // Format day digits
    let d = "__/";
    if (day.length === 2) {
      // Day has two digits and is complete
      d = day + "/";
    } else if (day.length === 1) {
      if (['0', '1', '2', '3'].includes(day) && year !== "") {
        // Day has one digit and year is empty => is ambiguous
        d = day;
      } else {
        // Day has one digit and is not ambiguous
        d = "0" + day + "/";
      }
    }

    // Format year digits
    let y = "____";
    if (year != undefined && year != '') {
      y = year;
    }
    
    // Set the formatted date back to the form field
    return `${m}${d}${y}`;
  }
}
